import type { EventInput } from "@fullcalendar/core";

import type { BriefCalendarEvent, CampaignBriefStatus } from "@/hooks/api/use-campaign-briefs";

import { REGIONS, STATUS_COLORS, STATUS_LABELS } from "../campaign-briefs/constants";

const DAY_MS = 24 * 60 * 60 * 1000;

/** The status chip's colour as CSS variables, so the calendar follows the light and dark schemes. */
function statusColors(status: CampaignBriefStatus): { main: string; contrast: string } {
	const color = STATUS_COLORS[status] ?? "default";
	return color === "default"
		? { main: "var(--mui-palette-neutral-500)", contrast: "var(--mui-palette-common-white)" }
		: { main: `var(--mui-palette-${color}-main)`, contrast: `var(--mui-palette-${color}-contrastText)` };
}

/** "SGT/WIB/JST": the regions' own clocks, each of which reads the time on its own. */
const clocks = (regions: string[]) =>
	regions.map((code) => REGIONS.find(({ value }) => value === code)?.clock ?? code).join("/");

/** "09:00–12:00, 13:00–17:00": the day's dialling hours, as wall clocks. */
const hoursLabel = (hours: BriefCalendarEvent["hours"]) =>
	hours.length > 0 ? hours.map(({ from, to }) => `${from}–${to}`).join(", ") : "No hours";

/**
 * One calendar entry for one dialling date. It is all-day on its local date because the hours are
 * wall clocks in every region the request names: 09:00 is 09:00 in Singapore and 09:00 in Tokyo,
 * which is no single moment a timed event could sit at, so nothing is converted. The hours lead the
 * title instead, which also makes title order time order. First dials are filled, redials outlined.
 */
export function toCalendarEvent(event: BriefCalendarEvent, { showAccount = true } = {}): EventInput {
	const { main, contrast } = statusColors(event.status);
	const redial = event.kind === "redial";
	return {
		id: `${event.briefId}:${event.kind}:${event.date}`,
		title: `${hoursLabel(event.hours)} · ${event.title} (${clocks(event.regions)})`,
		start: event.date,
		allDay: true,
		backgroundColor: redial ? "transparent" : main,
		borderColor: main,
		textColor: redial ? main : contrast,
		classNames: [redial ? "brief-redial" : "brief-dial"],
		extendedProps: {
			briefId: event.briefId,
			tooltip: [
				...(showAccount ? [event.accountName ?? "Unknown client"] : []),
				redial ? "Redial" : "First dial",
				STATUS_LABELS[event.status] ?? event.status,
			].join(" · "),
		},
	};
}

/** FullCalendar's visible range (start inclusive, end exclusive) as the API's inclusive dates. */
export function calendarRange(start: string, end: string): { from: string; to: string } {
	const last = new Date(Date.parse(`${end.slice(0, 10)}T00:00:00Z`) - DAY_MS);
	return { from: start.slice(0, 10), to: last.toISOString().slice(0, 10) };
}
